import React from "react";
import { Box, Typography, Divider, Alert, Chip } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { TeacherResponseDto } from "../../shared/dtos/teacher/TeacherResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewTeacherModalProps {
  open: boolean;
  onClose: () => void;
  teacher: TeacherResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewTeacherModal: React.FC<ViewTeacherModalProps> = ({
  open,
  onClose,
  teacher,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Teacher Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!teacher) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Teacher data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <DetailItem
            label="Teacher ID"
            value={
              <Chip
                size="small"
                label={teacher.teacherId || "N/A"}
                variant="outlined"
              />
            }
          />
          <DetailItem
            label="Full Name"
            value={
              `${teacher.firstName} ${teacher.middleName} ${teacher.lastName}` ||
              "N/A"
            }
          />
          <DetailItem label="Email" value={teacher.email || "N/A"} />
          <DetailItem
            label="Phone Number"
            value={teacher.primaryPhoneNumber || "N/A"}
          />
          <DetailItem label="Gender" value={teacher.gender || "N/A"} />
          <DetailItem
            label="Date of Joining"
            value={formatDateForDisplay(teacher.dateOfJoining)}
          />
          <DetailItem
            label="Qualifications"
            value={teacher.qualifications || "N/A"}
          />
          <DetailItem label="Branch" value={teacher.branchName || "N/A"} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Address Information
        </Typography>

        {teacher.address ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <DetailItem
              label="Street Line 1"
              value={teacher.address.streetLine1}
            />
            <DetailItem
              label="Street Line 2"
              value={teacher.address.streetLine2 || "N/A"}
            />
            <DetailItem label="City" value={teacher.address.city} />
            <DetailItem
              label="State/Province"
              value={teacher.address.stateOrProvince}
            />
            <DetailItem
              label="Postal Code"
              value={teacher.address.postalCode}
            />
            <DetailItem label="Country" value={teacher.address.country} />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No address information available.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Teacher Details" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewTeacherModal;
